import { useState, useEffect } from 'react';
import { Bell, Mail, Power, PowerOff, Plus, Save, X, PlayCircle } from 'lucide-react';
import Button from '../ui/Button';
import RuleConditionBuilder from './RuleConditionBuilder';
import RuleActionEditor from './RuleActionEditor';
import sensorRulesService, {
  type SensorRule,
  type RuleAction,
  type RuleCondition,
  type ConditionMetric,
  type RuleTestResult,
} from '../../services/sensorRules';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
} from '../../services/espnowIrrigation';
import subscriptionService from '../../services/subscription';
import type { LicensedUser } from '../../types/subscription';
import type { SensorChannel } from '../../services/sensorDevices';
import { useAuth } from '../../context/AuthContext';

interface Props {
  deviceId: string;
  channel: SensorChannel;
  // When provided, edit mode; otherwise create
  existing?: SensorRule;
  onSaved: (rule: SensorRule) => void;
  onCancel: () => void;
}

const ACTION_TYPES: Array<{ value: RuleAction['type']; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'notify_in_app', label: 'In-app notification', icon: Bell },
  { value: 'notify_email', label: 'Email', icon: Mail },
  { value: 'relay_on', label: 'Turn relay ON', icon: Power },
  { value: 'relay_off', label: 'Turn relay OFF', icon: PowerOff },
];

function emptyAction(type: RuleAction['type']): RuleAction {
  switch (type) {
    case 'notify_in_app':
      return { type, user_ids: 'all_admins', title: '', body: '' };
    case 'notify_email':
      return { type, user_ids: 'all_admins', subject: '', body: '' };
    case 'relay_on':
      return { type, controller_id: '', router_mac: '', relay_index: 0, duration_sec: 60 };
    case 'relay_off':
      return { type, controller_id: '', router_mac: '', relay_index: 0 };
  }
}

export default function SensorRuleForm({ deviceId, channel, existing, onSaved, onCancel }: Props) {
  const { user: authUser } = useAuth();
  const hasTank = !!(channel.config && (channel.config as Record<string, unknown>).tank);

  const [name, setName] = useState(existing?.name || '');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [condition, setCondition] = useState<RuleCondition>(
    existing?.condition || { all: [{ metric: 'value', op: 'lt', value: 0 }] }
  );
  const [useHysteresis, setUseHysteresis] = useState(!!existing?.hysteresis);
  const [hysteresis, setHysteresis] = useState<RuleCondition>(
    existing?.hysteresis || { all: [{ metric: 'value', op: 'gt', value: 0 }] }
  );
  const [cooldownSec, setCooldownSec] = useState(existing?.cooldown_sec ?? 300);
  const [actions, setActions] = useState<RuleAction[]>(existing?.actions || []);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<RuleTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lookups
  // Notification recipients are restricted to holders of an irrigation
  // licence — not the whole employee list.
  const [users, setUsers] = useState<LicensedUser[]>([]);
  const [controllers, setControllers] = useState<EspNowController[]>([]);
  const [routers, setRouters] = useState<EspNowRouter[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [usersRes, ctrlRes] = await Promise.all([
          subscriptionService.getModuleLicensedUsers('irrigation').catch(() => null),
          espnowService.getControllers().catch(() => [] as EspNowController[]),
        ]);
        if (!mounted) return;
        const licensedUsers: LicensedUser[] =
          usersRes && usersRes.success && Array.isArray(usersRes.data) ? usersRes.data : [];
        const orgId = authUser?.organisation_id;
        setUsers(
          orgId != null
            ? licensedUsers.filter(u => u.organisation_id === orgId)
            : licensedUsers
        );
        setControllers(ctrlRes);
        // Preload routers for every controller so the dropdowns cascade
        const allRouters: EspNowRouter[] = [];
        for (const c of ctrlRes) {
          try {
            const rs = await espnowService.getRouters(c.controller_id);
            allRouters.push(...rs);
          } catch {
            // skip failed fetches
          }
        }
        if (mounted) setRouters(allRouters);
      } catch {
        // non-fatal
      }
    })();
    return () => { mounted = false; };
  }, [authUser?.organisation_id]);

  const availableMetrics: Array<{ key: ConditionMetric; label: string; unit?: string; disabled?: boolean; reason?: string }> = [
    { key: 'value', label: 'Reading value', unit: channel.type_unit || undefined },
    {
      key: 'tank_percent',
      label: 'Tank % full',
      unit: '%',
      disabled: !hasTank,
      reason: hasTank ? undefined : 'Channel has no tank configuration',
    },
    {
      key: 'tank_liters',
      label: 'Tank volume',
      unit: 'L',
      disabled: !hasTank,
      reason: hasTank ? undefined : 'Channel has no tank configuration',
    },
  ];

  function addAction(type: RuleAction['type']) {
    setActions(prev => [...prev, emptyAction(type)]);
  }
  function updateAction(idx: number, next: RuleAction) {
    setActions(prev => prev.map((a, i) => (i === idx ? next : a)));
  }
  function removeAction(idx: number) {
    setActions(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (actions.length === 0) {
      setError('Add at least one action');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const commonFields = {
        name: name.trim(),
        enabled,
        condition,
        actions,
        cooldown_sec: cooldownSec,
        hysteresis: useHysteresis ? hysteresis : null,
      };
      const saved = existing
        ? await sensorRulesService.updateRule(existing.id, commonFields)
        : await sensorRulesService.createRule({
            device_id: deviceId,
            channel_index: channel.channel_index,
            channel_kind: channel.channel_kind,
            ...commonFields,
          });
      onSaved(saved);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save rule';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!existing) {
      setError('Save the rule first to test it');
      return;
    }
    setTesting(true);
    setError(null);
    try {
      const result = await sensorRulesService.testRule(existing.id);
      setTestResult(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Test failed';
      setError(msg);
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2">
        <h4 className="font-medium">{existing ? 'Edit rule' : 'New rule'}</h4>
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <label className="flex-1">
          <span className="text-xs text-gray-600">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tank low — refill"
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
        <label className="inline-flex items-center gap-1.5 mt-5 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </div>

      <div className="bg-white border border-gray-200 rounded p-3">
        <RuleConditionBuilder
          value={condition}
          onChange={setCondition}
          availableMetrics={availableMetrics}
          label="Trigger condition"
        />
      </div>

      <div>
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={useHysteresis}
            onChange={(e) => setUseHysteresis(e.target.checked)}
          />
          Use separate disarm condition (hysteresis)
        </label>
        {useHysteresis && (
          <div className="mt-2 bg-white border border-gray-200 rounded p-3">
            <RuleConditionBuilder
              value={hysteresis}
              onChange={setHysteresis}
              availableMetrics={availableMetrics}
              label="Disarm when"
            />
            <p className="text-[11px] text-gray-500 mt-2">
              Without hysteresis, the rule re-arms as soon as the trigger condition becomes false.
              With hysteresis, set a different threshold to reset — e.g. trigger on &lt; 20%, disarm on &gt; 30%.
            </p>
          </div>
        )}
      </div>

      <label className="block">
        <span className="text-xs text-gray-600">Cooldown (seconds)</span>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="number"
            min={0}
            max={86400}
            value={cooldownSec}
            onChange={(e) => setCooldownSec(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm w-32"
          />
          <span className="text-xs text-gray-500">{humaniseSeconds(cooldownSec)}</span>
        </div>
      </label>

      <div>
        <div className="text-xs font-medium text-gray-600 mb-2">Actions</div>
        <div className="space-y-2">
          {actions.length === 0 && (
            <div className="text-xs text-gray-400 italic">No actions yet.</div>
          )}
          {actions.map((a, i) => (
            <RuleActionEditor
              key={i}
              action={a}
              onChange={(next) => updateAction(i, next)}
              onRemove={() => removeAction(i)}
              users={users}
              controllers={controllers}
              routers={routers}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {ACTION_TYPES.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => addAction(t.value)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
              >
                <Icon className="w-3.5 h-3.5" />
                <Plus className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {testResult && (
        <div className={`p-3 border rounded text-sm ${
          testResult.would_fire
            ? 'bg-green-50 border-green-300 text-green-800'
            : 'bg-gray-50 border-gray-300 text-gray-700'
        }`}>
          <div className="font-medium mb-1">
            {testResult.would_fire ? 'Would fire now' : 'Would NOT fire'}
          </div>
          <div className="text-xs space-y-0.5">
            <div>Latest value: {testResult.latest_value ?? '—'}</div>
            {testResult.metrics.tank_percent !== undefined && testResult.metrics.tank_percent !== null && (
              <div>Tank %: {testResult.metrics.tank_percent?.toFixed(1)}%</div>
            )}
            {testResult.metrics.tank_liters !== undefined && testResult.metrics.tank_liters !== null && (
              <div>Tank liters: {testResult.metrics.tank_liters?.toFixed(1)} L</div>
            )}
            <div>Primary condition: {testResult.primary_true ? 'true' : 'false'}</div>
            <div>Currently armed: {testResult.current_state ? 'yes' : 'no'}</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-blue-100">
        <Button onClick={handleSave} disabled={saving} size="sm">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
        </Button>
        {existing && (
          <Button onClick={handleTest} disabled={testing} size="sm" variant="outline">
            <PlayCircle className="w-4 h-4 mr-1" /> {testing ? 'Testing…' : 'Test'}
          </Button>
        )}
        <Button onClick={onCancel} size="sm" variant="ghost">Cancel</Button>
      </div>
    </div>
  );
}

function humaniseSeconds(s: number): string {
  if (!s || s <= 0) return 'no cooldown';
  if (s < 60) return `${s} sec`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} hr`;
}
